import { Box, Button, Grid, useTheme } from "@mui/material";
import {
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
	TAX_PROFILE_LIST_SCREEN_SLUG,
} from "../../../common/constants";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, useNavigateToLogout } from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import DeleteIcon from "@mui/icons-material/Delete";
import { IAppState } from "../../../store/reducers";
import { IGetListRequest } from "../../../models";
import { PrimeDataGrid } from "../../PrimeDataGrid";
import { TaxProfileService } from "../../../services/TaxProfileService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

// import { EnhancedDDLBulkAction } from "@websential/ws-react-ui/lib/components/EnhancedDDLBulkAction";
// import { EnhancedDialog } from "@websential/ws-react-ui/lib/components/EnhancedDialog";
// import { ErrorState } from "@websential/ws-react-ui/lib/components/ErrorState/ErrorState";
// import { StatusChip } from "@websential/ws-react-ui/lib/components/StatusChip";
















export interface ITaxProfileList { }
const TAX_PROFILE_LIST_QUERY_KEY: any = "tax-profile-list";

const useTaxProfiles = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["tax-profile-list", params],
		queryFn: (data) => {
			return TaxProfileService.getList(params, navigateToLogout);
		},
		refetchOnWindowFocus: false
	});

export const TaxProfileList: React.FC<ITaxProfileList> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const navigateToLogout = useNavigateToLogout();
	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "role_name",
		order_direction: "asc",
		page: 1,
		size: INITIAL_PAGE_SIZE,
	});

	const { isPending, data } = useTaxProfiles(queryParams, navigateToLogout); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const taxProfileList: any = data;


	const upadtedTaxProfileListItems = useMemo(() => {
		return (
			taxProfileList &&
			taxProfileList.items &&
			taxProfileList?.items.map((item: any) => ({
				...item,
				effectiveDate:
					item.effectiveDate !== "" && null
						? formatDate(item.effectiveDate)
						: item.effectiveDate,
			}))
		);
	}, [taxProfileList]);

	const { message, componentState } = useSelector(
		(state: IAppState) => state.viewState
	);

	// const { accountantList } = useSelector(
	//   (state: IAppState) => state.accountant
	// );

	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [taxProfileId, setTaxProfileId] = useState<number>(0);

	// Bulk Delete
	const [bulkDelete, setBulkDelete] = useState<boolean>(false);
	const [bulkTaxProfileId, setBulkTaxProfileId] = useState<any>({});
	const [bulkTaxProfileDelete, setBulkTaxProfileDelete] =
		useState<boolean>(false);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);

		let path = `/tax-profile/setup/${params.id}`;
		navigate(path);
	}, []);

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	// For Delete Row
	const deleteTaxProfileField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let payload: any = {
				taxProfileId: taxProfileId,
				navigateToLogout: navigateToLogout,
			};
			deleteTaxProfileById(payload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setApiAction("delete");
		setTaxProfileId(params.row.id);

		// alert(`Delete row with ID: ${params.row.id} has been clicked!`);
	}, []);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteTaxProfileById } = useMutation({
		mutationFn: TaxProfileService.delTaxProfile,
		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the Year fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});

				return queryClient.invalidateQueries(TAX_PROFILE_LIST_QUERY_KEY);
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	}
	);

	const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		setStatusValue(event.target.value as string);
		setApiAction("updateStatus");
		setDialogOpen(true);
	};

	//bulk delete

	const { isPending: isBulkDeleting, mutate: deleteBulkTaxProfile } =
		useMutation({
			mutationFn: TaxProfileService.delBulkTaxProfile,
			onSuccess: (res: any | string | IServerError | IResponse) => {
				if (typeof res === "object") {
					// remove the cache of the Year fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong.",
						severity: res.success === false ? "error" : "success",
					});

					setBulkTaxProfileDelete(false);
					return queryClient.invalidateQueries(TAX_PROFILE_LIST_QUERY_KEY);
				} else {
					setSnackbarData({
						open: true,
						message: res.message
							? res.message
							: "Something went wrong, try again later!",
						severity: res.success === false ? "error" : "success",
					});
				}
				setBulkTaxProfileDelete(false);
			},
			onError: (err: any) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
				setBulkTaxProfileDelete(false);
			},
		});

	// For Bulk Delete
	const deleteBulkTaxProfileField = () => {
		if (bulkDelete) {
			setBulkDelete(false);
			let payload = {
				bulkTaxProfileId: bulkTaxProfileId,
				navigateToLogout: navigateToLogout,
			};
			deleteBulkTaxProfile(payload);
			setDialogOpen(false);
		}
	};

	const onRowMultipleDeleteClicked = (params: any) => {

		let bulkDeletePayload = {
			TaxProfileIds: { TaxProfileIds: params },
		};
		setDialogOpen(true);
		setBulkDelete(true);
		setApiAction("delete");
		setBulkTaxProfileId(bulkDeletePayload);
		setBulkTaxProfileDelete(true);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);
		if (selectedItems.length) {
			return (
				<Box sx={{ marginTop: theme.spacing(1) }}>
					<Button
						onClick={() => onRowMultipleDeleteClicked(selectedItems)}
						variant="contained"
						color="error"
					>
						Delete
					</Button>
				</Box>
			);
		}
		return (
			<Box sx={{ marginTop: theme.spacing(1) }}>
				<RouterLinkButton to="/tax-profile/setup" text="Add Tax Profile" />
			</Box>
		);
	}, []);

	const expandedLabels = ["Misc.", "Tax Profile"];
	const expandedBanckListItem = "List";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Tax Profile" &&
				item.label === expandedBanckListItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: TAX_PROFILE_LIST_SCREEN_SLUG,
				companyName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return taxProfileList && taxProfileList.items.length === 0 ? (
		<EmptyList
			showImage={true}
			imagePath={image}
			heading={`There's nothing! Add Tax Profile to proceed`}
			content={`You can add tax profile by directly clicking on add tax profile button.`}
			testId={"tax profile-empty-list"}
			showAddButton={true}
			addButtonLabel={"Add Tax Profile"}
			navigateToAdd={"/tax-profile/setup"}
			showImportButton={false}
			showImportXmlButton={false}
		/>
	) : (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ width: "100%" }}>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={
						bulkTaxProfileDelete === true
							? deleteBulkTaxProfileField
							: deleteTaxProfileField
					}
					cancelActionHandler={onCloseHandler}
					testId="tax-profile-tab-confirmation-dialog"
				/>
				{/* <EnhancedDataGrid
        columns={taxProfileList ? taxProfileList.headerColumns : []}
        rows={taxProfileList?.items}
        selectionEnabled={true}
        autoHeight={true}
        allowSearching={true}
        rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
        selectionToolbar={selectionToolbar}
        onRowEditClicked={onRowEditClicked}
        onRowDeleteClicked={onRowDeleteClicked}
        // initialPage={0}
        initialPageSize={INITIAL_PAGE_SIZE}
        disableColumnMenu={true}
        showRowActionsColumn={true}
        autoPageSize={false} //
        showIndexColumn={true}
        testId="tax-profile-list-grid"
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        onRequestParamsChange={onRequestParamsChange}
        totalRowsCount={taxProfileList?.total}
        isPending={isPending || isDeleting}
        rowIdGetter={(row) => row.id}
        searchDebouncingDelay={SEARCH_DEBOUNCE_DELAY_IN_SECONDS}
        shouldMask={false}
      /> */}

				<PrimeDataGrid
					columns={taxProfileList ? taxProfileList.headerColumns : []}
					rows={
						taxProfileList && upadtedTaxProfileListItems
							? upadtedTaxProfileListItems
							: []
					}
					showIndexColumn={true}
					showChecboxColumn={true}
					showSettingColumn={true}
					onRowEditClicked={onRowEditClicked}
					onRowDeleteClicked={onRowDeleteClicked}
					isLoading={
						isPending || isDeleting
						// || isBulkDeleting
					}
					// onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						taxProfileList && taxProfileList.total ? taxProfileList.total : 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					selectionToolbar={selectionToolbar}
					// selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
					searchFieldPlaceholder={"Search..."}
				// changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
				//   setzeroTaxProfile(value)
				// }
				/>
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"tax-profile-tab-list-snackbar"}
				open={snackbarData.open}
			/>
		</>
	);
};
